import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ProjectCard = ({ project, onDelete }) => {
  const navigate = useNavigate();
  const [showMenu, setShowMenu] = React.useState(false);
  const menuRef = React.useRef(null);

  // Close menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef?.current && !menuRef?.current?.contains(event?.target)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  const getStatusColor = (status) => {
    const colors = {
      'in-progress': 'text-primary bg-primary/10',
      'draft': 'text-warning bg-warning/10',
      'completed': 'text-success bg-success/10',
      'paused': 'text-muted-foreground bg-muted'
    };
    return colors?.[status] || colors?.draft;
  };

  const getStatusIcon = (status) => {
    const icons = {
      'in-progress': 'Clock',
      'draft': 'FileEdit',
      'completed': 'CheckCircle',
      'paused': 'Pause'
    };
    return icons?.[status] || 'File';
  };

  const getStageInfo = (stage) => {
    const stages = {
      'topic-research': { label: 'Topic Research', icon: 'Target', color: 'text-primary' },
      'outline': { label: 'Outline Creation', icon: 'List', color: 'text-secondary' },
      'writing': { label: 'Writing', icon: 'FileText', color: 'text-accent' },
      'formatting': { label: 'Formatting', icon: 'Layout', color: 'text-success' },
      'cover-design': { label: 'Cover Design', icon: 'Image', color: 'text-warning' },
      'publishing': { label: 'Publishing Prep', icon: 'Send', color: 'text-primary' }
    };
    return stages?.[stage] || stages?.['topic-research'];
  };

  const stageInfo = getStageInfo(project?.currentStage);
  const formattedDate = new Date(project.lastModified)?.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });

  const handleResumeWork = () => {
    navigate('/ai-agent-workflow-hub', { state: { projectId: project?.id } });
  };

  const handleViewDetails = () => {
    navigate('/book-project-details', { state: { projectId: project?.id } });
  };

  const handleEditProject = () => {
    setShowMenu(false);
    // Navigate to edit page or open edit modal
    navigate('/book-project-details', { state: { projectId: project?.id, mode: 'edit' } });
  };

  const handleDeleteProject = () => {
    setShowMenu(false);
    // Show confirmation dialog before deleting
    if (window.confirm(`Are you sure you want to delete "${project?.title}"? This action cannot be undone.`)) {
      // Call the onDelete prop passed from parent to actually delete the project
      if (onDelete) {
        onDelete(project?.id);
      }
    }
  };

  const toggleMenu = (e) => {
    e?.stopPropagation();
    setShowMenu(!showMenu);
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden">
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-heading font-semibold text-foreground mb-2 truncate">
              {project?.title}
            </h3>
            <div className="flex items-center space-x-2">
              <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-md text-xs font-medium ${getStatusColor(project?.status)}`}>
                <Icon name={getStatusIcon(project?.status)} size={14} />
                <span className="capitalize">{project?.status?.replace('-', ' ')}</span>
              </span>
              <span className="text-xs text-muted-foreground">
                {project?.category}
              </span>
            </div>
          </div>
          <div className="relative flex-shrink-0" ref={menuRef}>
            <button
              onClick={toggleMenu}
              className="p-2 hover:bg-muted rounded-md transition-colors duration-200"
              aria-label="Project options"
            >
              <Icon name="MoreVertical" size={20} className="text-muted-foreground" />
            </button>
            
            {showMenu && (
              <div className="absolute right-0 top-full mt-1 w-48 bg-card border border-border rounded-lg shadow-lg py-1 z-10">
                <button
                  onClick={handleEditProject}
                  className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-foreground hover:bg-muted transition-colors duration-150"
                >
                  <Icon name="Edit" size={16} />
                  <span>Edit Project</span>
                </button>
                <button
                  onClick={handleDeleteProject}
                  className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-destructive hover:bg-destructive/10 transition-colors duration-150"
                >
                  <Icon name="Trash2" size={16} />
                  <span>Delete Project</span>
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2">
                <Icon name={stageInfo?.icon} size={16} className={stageInfo?.color} />
                <span className="text-sm font-medium text-foreground">
                  {stageInfo?.label}
                </span>
              </div>
              <span className="text-sm font-mono text-muted-foreground">
                {project?.progress}%
              </span>
            </div>
            <div className="relative w-full h-2 bg-muted rounded-full overflow-hidden">
              <div
                className="absolute top-0 left-0 h-full bg-primary transition-all duration-400 ease-in-out"
                style={{ width: `${project?.progress}%` }}
              />
            </div>
          </div>

          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <div className="flex items-center space-x-1">
              <Icon name="Calendar" size={14} />
              <span>Last modified: {formattedDate}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Icon name="FileText" size={14} />
              <span>{project?.wordCount?.toLocaleString()} words</span>
            </div>
          </div>
        </div>
      </div>
      <div className="px-6 py-4 bg-muted/30 border-t border-border flex items-center justify-between">
        <Button
          variant="ghost"
          size="sm"
          iconName="Eye"
          iconPosition="left"
          onClick={handleViewDetails}
        >
          View Details
        </Button>
        <Button
          variant="default"
          size="sm"
          iconName="Play"
          iconPosition="left"
          onClick={handleResumeWork}
          disabled={project?.status === 'completed'}
        >
          {project?.status === 'completed' ? 'Completed' : 'Resume Work'}
        </Button>
      </div>
    </div>
  );
};

export default ProjectCard;